import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { PlayCircle, Gift } from "lucide-react";
import { toast } from "sonner";
import { isNative, showRewarded } from "../lib/admob";
import { useAuth } from "../context/AuthContext";

// Rewarded-ad trigger.
//   - Native (Capacitor + AdMob): shows the real Google rewarded ad. Passes
//     user_id + rewardType via SSV custom_data so backend grants the reward
//     server-side once Google signs-off.
//   - Web/dev: shows the 3-second mock countdown (labelled MOCKED AD) and
//     invokes the provided onReward callback directly.
export default function MockAdDialog({
  open, onOpenChange, title, description, onReward, rewardLabel,
  rewardType = "coins", onWatchingChange,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [watching, setWatching] = useState(false);
  const [remaining, setRemaining] = useState(3);
  // Prevents double-trigger from React StrictMode / dialog re-open races.
  // Without this the auto-effect could call handleWatch twice and the
  // second showRewarded would fail with "ad already showing", leaving
  // the dialog stuck on a black AdMob overlay (tester complaint v35
  // "ad gets stuck when refilling energy").
  const inFlightRef = useRef(false);

  // Keep the latest callbacks in a ref so the web countdown effect below can
  // depend ONLY on [watching, remaining]. Previously the effect listed
  // onReward/onOpenChange/onWatchingChange in its deps; parents pass fresh
  // inline closures every render, so the effect re-fired on every parent
  // render, restarting the countdown and firing a setState storm that
  // crashed the app with "Maximum update depth exceeded" right after the
  // ad closed (QA iter148).
  const cbRef = useRef({});
  cbRef.current = { onReward, onOpenChange, onWatchingChange };

  // Notify parent immediately (synchronously) when the watch state flips so
  // it can pause the game timer for the duration of the ad. Earlier we used
  // a useEffect on `watching` which fired one render LATE — on Android the
  // rewarded ad started playing before the pause flag flipped, so the
  // game clock kept ticking through the first second(s) of the ad
  // (tester complaint v27: "clock still ticking during ad").

  // Auto-start the ad on native the moment the dialog opens. The user
  // already confirmed "Watch ad" on the previous out-of-stock dialog —
  // forcing them to tap a second "Watch ad" button on top of the AdMob
  // overlay is an extra screen for no benefit (tester complaint v31:
  // "this additional screen is not necessary"). On web we still show
  // the 3s mock-countdown UI so the user can SEE the simulated ad.
  useEffect(() => {
    if (open && isNative() && !inFlightRef.current) {
      handleWatch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleWatch = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    onWatchingChange?.(true);  // pause game timer NOW, not after re-render
    setWatching(true);
    if (isNative()) {
      // Iter61 — Tester demand: "Before showing an ad, check if it is
      // loaded. If not ready yet, show 'Loading ad, please wait...' and
      // wait for the load event rather than immediately showing an
      // error." We surface a dismissible toast that auto-clears once
      // the ad starts playing (or fails). Duration must be ≥ 3000 ms
      // so the user has time to read it.
      //
      // Iter74 — Tester report "Ad loading forever!" with screenshot of
      // the toast persisting on the dashboard after navigation. Two
      // belt-and-braces hardens added:
      //   1. `duration: 12_000` instead of 15 s — sonner auto-dismisses
      //      after this even if our explicit dismiss never runs.
      //   2. A separate force-dismiss `setTimeout(12_000)` that calls
      //      `toast.dismiss(loadingToastId)` no matter what. Survives
      //      navigation, hung promises, and bridge crashes.
      const loadingToastId = toast.loading(
        t("ads.loading_ad", "Loading ad, please wait…"),
        { duration: 12000 },
      );
      const forceDismissId = setTimeout(() => {
        toast.dismiss(loadingToastId);
      }, 12000);
      // Hard timeout so a stalled AdMob load doesn't leave the user
      // staring at a frozen overlay. Iter78 — was 25 s; testers
      // reported "Ads loading forever". 14 s is a hard ceiling that
      // is still below the inner showRewarded timeout (15 s) so the
      // outer dialog ALWAYS wins the race and cleans up. After this
      // we close the dialog, clear adPaused, and the user can resume
      // playing instead of staring at the loading toast forever.
      // Iter87 — the OUTER guard must only cover the LOADING phase. A
      // rewarded ad runs 15-30 s; if this timeout fired mid-watch it
      // closed the dialog and RESUMED THE GAME TIMER while the ad was
      // still playing (and pre-iter87 tore down the reward listener → no
      // reward). We flip `adShowingRef` the moment the ad appears (via the
      // onShowing callback) so the guard becomes a no-op and showRewarded
      // resolves naturally when the ad is dismissed.
      const adShowingRef = { current: false };
      const timeoutPromise = new Promise((resolve) =>
        setTimeout(() => {
          if (adShowingRef.current) return; // ad is on screen — let it finish
          resolve({ rewarded: false, timeout: true });
        }, 14000),
      );
      let result = { rewarded: false };
      try {
        // Iter62 — Pass onReward INTO showRewarded so it fires the
        // moment Google's Rewarded event hits the JS bridge, decoupled
        // from the Dismissed event lifecycle. The dialog still waits
        // for Dismissed to close itself visually.
        // Iter87 — onShowing dismisses the loading toast + disarms the
        // outer timeout the instant the ad is actually visible.
        result = await Promise.race([
          showRewarded({
            userId: user?.user_id,
            rewardType,
            onReward,
            onShowing: () => {
              adShowingRef.current = true;
              clearTimeout(forceDismissId);
              toast.dismiss(loadingToastId);
            },
          }),
          timeoutPromise,
        ]);
      } catch (e) {
        result = { rewarded: false, error: String(e) };
      } finally {
        clearTimeout(forceDismissId);
        toast.dismiss(loadingToastId);
      }
      // Iter73 — Removed the developer-only "🎬 ad ok · rewarded=true ·
      // shown=Xms" toast. It was useful while wiring AdMob up but
      // tester feedback was unambiguous: "Don't need this message -
      // ad ok. rewarded=true".
      //
      // Iter73 — Only surface the failure toast on a GENUINE error
      // (`result.error`). Previously we also showed it whenever the
      // outcome wasn't `rewarded` AND wasn't `dismissed`, which fired
      // a false "Ad couldn't load" message whenever the SDK reported
      // an outcome we hadn't categorised (e.g. timeout race vs Rewarded
      // event ordering). With strict reward semantics in place, any
      // outcome without Rewarded is silently treated as "no reward,
      // no error" — the player simply doesn't get the bonus.
      // Iter78 — also surface a friendly toast when the OUTER race
      // timed out so the user understands the dialog closed itself.
      if (result?.error) {
        toast.error(t("ads.failed_toast"), { duration: 3500 });
      } else if (result?.timeout) {
        toast.error(t("ads.failed_toast"), { duration: 3500 });
      }
      onWatchingChange?.(false);  // resume game timer
      setWatching(false);
      inFlightRef.current = false;
      onOpenChange(false);
    }
    // Web path uses the effect below for the 3s countdown.
  };

  useEffect(() => {
    if (!watching || isNative()) return;
    if (remaining <= 0) {
      (async () => {
        await cbRef.current.onReward?.();
        cbRef.current.onWatchingChange?.(false);  // resume game timer
        setWatching(false);
        setRemaining(3);
        cbRef.current.onOpenChange?.(false);
      })();
      return;
    }
    const t = setTimeout(() => setRemaining(remaining - 1), 900);
    return () => clearTimeout(t);
  }, [watching, remaining]);

  useEffect(() => {
    if (!open) {
      // If dialog was closed (escape / outside-click) while we were watching,
      // make sure to resume the game timer.
      if (watching) onWatchingChange?.(false);
      setWatching(false);
      setRemaining(3);
      inFlightRef.current = false;
    }
  }, [open]);

  // On native, render NOTHING — handleWatch is auto-triggered by the
  // useEffect above and the AdMob overlay takes over the screen. Rendering
  // a Dialog underneath was the "additional screen" testers complained
  // about (v31).
  if (isNative()) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mock-ad-dialog" className="rounded-3xl border-[#E6E2D8] bg-white">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl font-light">
            {title}
          </DialogTitle>
          <DialogDescription className="text-[#2C332A]/70">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="py-8 flex flex-col items-center justify-center text-[#2C332A]">
          {watching && !isNative() ? (
            <div className="text-center">
              <PlayCircle className="mx-auto mb-4 text-[#5F7161]" size={56} strokeWidth={1.2} />
              <p className="text-sm uppercase tracking-[0.2em] mb-2">MOCKED AD</p>
              <p className="text-3xl font-light tabular-nums">{remaining}s</p>
            </div>
          ) : watching && isNative() ? (
            <div className="text-center">
              <PlayCircle className="mx-auto mb-4 text-[#5F7161]" size={56} strokeWidth={1.2} />
              <p className="text-sm uppercase tracking-[0.2em]">Loading AdMob rewarded ad…</p>
            </div>
          ) : (
            <div className="text-center">
              <Gift className="mx-auto mb-4 text-[#D0A98F]" size={56} strokeWidth={1.2} />
              <p className="text-lg">{rewardLabel}</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            data-testid="mock-ad-cancel"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={watching}
          >
            Not now
          </Button>
          <Button
            data-testid="mock-ad-watch"
            onClick={handleWatch}
            disabled={watching}
            className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white"
          >
            {watching ? t("ads.watching") : t("daily_goal.watch_ad")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
